import {
    BadgeCheck,
    CheckCircle2,
    ChevronRight,
    Clock,
    ImagePlus,
    Info,
    Lock,
    Play,
    ShieldAlert,
    Star,
    UserRound,
    X,
} from "lucide-react";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { RootState } from "../../../store";
import {
    createReviewWithMedia,
    fetchMyReviewForProduct,
    updateCustomerReview,
} from "../../../Redux/products review/slice";
import { reviewType } from "../../../Redux/products review/types";
import { fetchMyOrders } from "../../../Redux/custmoer_orders/slice";
import {
    Order,
    CustomerDeliveryStatus,
    PaymentStatus,
} from "../../../Redux/custmoer_orders/types";
import { getCookie } from "../../../utility/getCookie";
import { uploadToCloudinary } from "../../../utility/uploadToCloudinary";
import { productReviewRatingLabels, productReviewTips } from "./ProductReviewData";
import { ProductReviewMedia, ProductReviewSubmission } from "./ProductReviewTypes";
import "./ProductReviewPage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import { useCart } from "../../../context/CartContext";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;
const MAX_MEDIA = 6;

const ProductReviewPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { productId } = useParams<{ productId?: string }>();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const { orders, loading: ordersLoading } = useAppSelector(
        (state: RootState) => state.customerOrders
    );
    const { user } = useAppSelector((state: RootState) => state.customerAuth);
    const { myReview, canEditMyReview, editDaysLeft } = useAppSelector(
        (state: RootState) => state.productsReview
    );

    const { cartCount } = useCart();

    const token = getCookie("access_token") || localStorage.getItem("access_token");
    const isLoggedIn = Boolean(
        user ||
        (token &&
            token !== "undefined" &&
            token !== "null" &&
            typeof token === "string" &&
            token.length > 20)
    );

    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState("");
    const [title, setTitle] = useState("");
    const [publicName, setPublicName] = useState("");
    const [media, setMedia] = useState<ProductReviewMedia[]>([]);
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchedProduct, setFetchedProduct] = useState<any>(null);
    const [initialPopulated, setInitialPopulated] = useState(false);

    // Fetch customer's own review for this product on load
    useEffect(() => {
        if (isLoggedIn && productId) {
            dispatch(fetchMyReviewForProduct(productId));
        }
    }, [dispatch, isLoggedIn, productId]);

    // Fetch product details
    useEffect(() => {
        if (!productId) return;
        axios
            .get(`${PRODUCT_BASE_URL}/e-commerce/catalog/products/${productId}`)
            .then((res) => {
                if (res.data?.data) {
                    setFetchedProduct(res.data.data);
                }
            })
            .catch((err) => console.error("Failed to load product for review:", err));
    }, [productId]);

    // Fetch orders if logged in to verify purchase
    useEffect(() => {
        if (isLoggedIn && orders.length === 0) {
            dispatch(fetchMyOrders({ page: 1, limit: 100 }));
        }
    }, [dispatch, isLoggedIn, orders.length]);

    // Populate publicName from user profile if not editing
    useEffect(() => {
        if (!initialPopulated && (user?.name || (user as any)?.firstName || (user as any)?.fullName)) {
            const fullName =
                user?.name ||
                (user as any)?.fullName ||
                `${(user as any).firstName || ""} ${(user as any).lastName || ""}`.trim();
            if (fullName) {
                setPublicName(fullName);
            }
        }
    }, [user, initialPopulated]);

    const existingReview = myReview || location.state?.existingReview || null;
    const hasExistingReview = Boolean(existingReview);
    const isEditMode = hasExistingReview && canEditMyReview;
    const isLockedMode = hasExistingReview && !canEditMyReview;

    // Pre-populate if customer has an existing review
    useEffect(() => {
        if (existingReview && !initialPopulated) {
            if (existingReview.rating) {
                setRating(Number(existingReview.rating));
            }
            const rawDesc = existingReview.description || "";
            const firstPeriod = rawDesc.indexOf(". ");
            if (firstPeriod > 0) {
                setTitle(rawDesc.slice(0, firstPeriod).trim());
                setReview(rawDesc.slice(firstPeriod + 2).trim());
            } else {
                setTitle(rawDesc);
            }
            if (existingReview.customerName) {
                setPublicName(existingReview.customerName);
            }
            const existingMedia: ProductReviewMedia[] = [];
            (existingReview.images || []).forEach((imgUrl: string, idx: number) => {
                if (typeof imgUrl === "string" && imgUrl.trim()) {
                    existingMedia.push({
                        id: `existing-img-${idx}`,
                        name: `Photo ${idx + 1}`,
                        type: "image",
                        previewUrl: imgUrl.trim(),
                        url: imgUrl.trim(),
                    });
                }
            });
            (existingReview.videos || []).forEach((vidUrl: string, idx: number) => {
                if (typeof vidUrl === "string" && vidUrl.trim()) {
                    existingMedia.push({
                        id: `existing-vid-${idx}`,
                        name: `Video ${idx + 1}`,
                        type: "video",
                        previewUrl: vidUrl.trim(),
                        url: vidUrl.trim(),
                    });
                }
            });
            if (existingMedia.length > 0) {
                setMedia(existingMedia);
            }
            setInitialPopulated(true);
        }
    }, [existingReview, initialPopulated]);

    // Match real qualifying order for this product
    const matchedOrder = useMemo<Order | null>(() => {
        const passedOrder = location.state?.matchedOrder;
        if (passedOrder) return passedOrder;

        if (!isLoggedIn || !Array.isArray(orders) || orders.length === 0) {
            return null;
        }

        const targetIds = [productId, fetchedProduct?._id, fetchedProduct?.ssin]
            .filter(Boolean)
            .map((id) => String(id).toLowerCase().trim());

        return (
            orders.find((order) => {
                const delivery = String(order.deliveryStatus || "").trim().toUpperCase();
                const payment = String(order.paymentStatus || "").trim().toUpperCase();

                if (
                    delivery === CustomerDeliveryStatus.CANCELLED ||
                    delivery === "CANCELLED" ||
                    payment === PaymentStatus.CANCELLED ||
                    payment === "CANCELLED" ||
                    payment === PaymentStatus.REFUNDED ||
                    payment === "REFUNDED" ||
                    payment === PaymentStatus.FAILED ||
                    payment === "FAILED"
                ) {
                    return false;
                }

                const hasProduct =
                    Array.isArray(order.productDetails) &&
                    order.productDetails.some((p) => {
                        const pid = String(p.productId || (p as any).id || (p as any)._id || "")
                            .toLowerCase()
                            .trim();
                        return targetIds.includes(pid);
                    });

                if (!hasProduct) return false;

                const isOrderCompleted =
                    delivery === CustomerDeliveryStatus.DELIVERED ||
                    delivery === "DELIVERED" ||
                    delivery === "COMPLETED";

                const isPaymentCompleted =
                    payment === PaymentStatus.COMPLETED ||
                    payment === "COMPLETED" ||
                    payment === "PAID" ||
                    payment === "SUCCESS" ||
                    payment === "CAPTURED";

                return isOrderCompleted || isPaymentCompleted;
            }) || null
        );
    }, [isLoggedIn, orders, productId, fetchedProduct, location.state]);

    const matchedProductDetail = useMemo(() => {
        if (!matchedOrder || !Array.isArray(matchedOrder.productDetails)) return null;
        const targetIds = [productId, fetchedProduct?._id, fetchedProduct?.ssin]
            .filter(Boolean)
            .map((id) => String(id).toLowerCase().trim());

        return (
            matchedOrder.productDetails.find((p) => {
                const pid = String(p.productId || (p as any).id || (p as any)._id || "")
                    .toLowerCase()
                    .trim();
                return targetIds.includes(pid);
            }) || matchedOrder.productDetails[0]
        );
    }, [matchedOrder, productId, fetchedProduct]);

    const displayTitle =
        fetchedProduct?.name || matchedProductDetail?.productName || "Purchased Product";
    const displayDescription =
        fetchedProduct?.description || fetchedProduct?.shortDescription || "-";
    const displayImage =
        fetchedProduct?.mainImage ||
        matchedProductDetail?.productImage ||
        (Array.isArray(fetchedProduct?.images) && fetchedProduct.images[0]) ||
        "";
    const orderRefId =
        matchedOrder?.orderNo ||
        matchedOrder?.orderId ||
        location.state?.orderNo ||
        location.state?.orderId ||
        "-";
    const orderPlacedDate = matchedOrder?.orderDate
        ? new Date(matchedOrder.orderDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : "-";

    const ratingLabel = rating > 0 ? productReviewRatingLabels[rating] : "Select a rating";

    useEffect(() => {
        return () => {
            media.forEach((file) => {
                if (file.previewUrl?.startsWith("blob:")) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            });
        };
    }, [media]);

    const openProduct = () => {
        const targetId = fetchedProduct?._id || productId;
        navigate(`/DetailPage/${targetId}`);
    };

    const handleMediaChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (isLockedMode) return;
        const files = Array.from(event.target.files ?? []);
        if (files.length === 0) return;
        const availableSlots = MAX_MEDIA - media.length;
        const acceptedFiles = files
            .filter(
                (file) =>
                    file.type.startsWith("image/") || file.type.startsWith("video/")
            )
            .slice(0, availableSlots);
        const nextMedia = acceptedFiles.map(
            (file, index): ProductReviewMedia => ({
                id: `${Date.now()}-${index}-${file.name}`,
                name: file.name,
                type: file.type.startsWith("video/") ? "video" : "image",
                previewUrl: URL.createObjectURL(file),
                file,
            })
        );
        setMedia((current) => [...current, ...nextMedia]);
        setError("");
        event.target.value = "";
    };

    const removeMedia = (id: string) => {
        if (isLockedMode) return;
        setMedia((current) => {
            const target = current.find((file) => file.id === id);
            if (target && target.previewUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(target.previewUrl);
            }
            return current.filter((file) => file.id !== id);
        });
    };

    const handleSubmit = async () => {
        if (isLockedMode) {
            setError("Reviews can only be updated within 5 days of submission.");
            return;
        }
        if (rating === 0) {
            setError("Please select a star rating.");
            return;
        }
        if (!title.trim()) {
            setError("Please add a title for your review.");
            return;
        }
        if (!publicName.trim()) {
            setError("Please enter your public name.");
            return;
        }
        if (!productId) {
            setError("Product identifier is missing.");
            return;
        }

        setSubmitting(true);
        setError("");

        try {
            const combinedDescription = review.trim()
                ? `${title.trim()}. ${review.trim()}`
                : title.trim();

            let newUploadedImages: string[] = [];
            let newUploadedVideos: string[] = [];

            if (media.length > 0) {
                const imageFiles = media
                    .filter((m) => m.type === "image" && m.file)
                    .map((m) => m.file as File);
                const videoFiles = media
                    .filter((m) => m.type === "video" && m.file)
                    .map((m) => m.file as File);

                const uploadOptions = {
                    getCookie: (name: string) => getCookie(name) || undefined,
                };

                if (imageFiles.length > 0) {
                    try {
                        newUploadedImages = await Promise.all(
                            imageFiles.map((file) => uploadToCloudinary(file, uploadOptions))
                        );
                    } catch (uploadErr) {
                        console.warn("Failed to upload review images:", uploadErr);
                    }
                }

                if (videoFiles.length > 0) {
                    try {
                        newUploadedVideos = await Promise.all(
                            videoFiles.map((file) => uploadToCloudinary(file, uploadOptions))
                        );
                    } catch (uploadErr) {
                        console.warn("Failed to upload review videos:", uploadErr);
                    }
                }
            }

            const existingImages = media
                .filter((m) => m.type === "image" && !m.file && m.previewUrl)
                .map((m) => m.previewUrl);
            const existingVideos = media
                .filter((m) => m.type === "video" && !m.file && m.previewUrl)
                .map((m) => m.previewUrl);

            const allImages = [...existingImages, ...newUploadedImages];
            const allVideos = [...existingVideos, ...newUploadedVideos];

            const reviewerName = publicName.trim();
            const reviewerEmail = user?.email || (user as any)?.emailId || "";
            const reviewerAvatar = user?.profilePicture || (user as any)?.avatar || "";

            const targetProductId = String(fetchedProduct?._id || productId);
            const productVariantId =
                location.state?.productVariantId ||
                (matchedProductDetail as any)?.variantId ||
                (matchedProductDetail as any)?.productVariantId ||
                undefined;
            const sellerSkuId =
                location.state?.sellerSkuId ||
                (matchedProductDetail as any)?.sellerSkuId ||
                (fetchedProduct as any)?.featuredOffer?.sellerSkuId ||
                undefined;
            const purchasedVariationSnapshot =
                (matchedProductDetail as any)?.variationAttributes ||
                (matchedProductDetail as any)?.attributes ||
                undefined;

            if (isEditMode && existingReview?._id) {
                await dispatch(
                    updateCustomerReview({
                        reviewId: String(existingReview._id),
                        data: {
                            rating,
                            description: combinedDescription,
                            images: allImages,
                            videos: allVideos,
                            customerName: reviewerName,
                            customerAvatar: reviewerAvatar,
                            status: "Published",
                        },
                    })
                ).unwrap();
            } else {
                await dispatch(
                    createReviewWithMedia({
                        productId: targetProductId,
                        rating,
                        description: combinedDescription,
                        reviewType: reviewType.CATALOG_PRODUCT,
                        images: allImages,
                        videos: allVideos,
                        productVariantId,
                        sellerSkuId,
                        purchasedVariationSnapshot,
                        isVerifiedPurchase: !!matchedOrder,
                        status: "Published",
                        customerName: reviewerName,
                        customerEmail: reviewerEmail,
                        customerAvatar: reviewerAvatar,
                    })
                ).unwrap();
            }

            setSubmitted(true);
        } catch (err: any) {
            const errorMessage =
                typeof err === "string"
                    ? err
                    : err?.message ||
                      err?.response?.data?.message ||
                      "Failed to submit review. Please ensure your purchase is complete and try again.";
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <>
                <div className="product-review-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <div className="product-review-mobile-navbar">
                    <MobileDetailNavbar cartCount={cartCount} fallbackPath="/Orders" />
                </div>
                <div className="product-review-desktop-secondary-navbar">
                    <HomeSecondaryNavbar
                        variant="detail"
                        browsingHistoryOpen={browsingHistoryOpen}
                        onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)}
                    />
                </div>
                <main className="product-review-page">
                    <div className="product-review-shell">
                        <section className="product-review-success">
                            <div className="product-review-success-icon">
                                <CheckCircle2 size={42} />
                            </div>
                            <span>REVIEW SUBMITTED</span>
                            <h1>Thanks for reviewing your purchase</h1>
                            <p>
                                Your review for <strong>{displayTitle}</strong> has been
                                submitted. After moderation, it will help other Salar customers
                                make an informed purchase decision.
                            </p>
                            <div>
                                {matchedOrder ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/MaxOrderDetails?id=${matchedOrder.orderId}`)}
                                    >
                                        Back to Order Details
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => navigate("/Orders")}
                                    >
                                        Back to Orders
                                    </button>
                                )}
                                <button type="button" onClick={() => openProduct()}>
                                    View Product
                                </button>
                            </div>
                        </section>
                    </div>
                </main>
            </>
        );
    }

    // Gating: If not logged in
    if (!isLoggedIn) {
        return (
            <>
                <div className="product-review-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <div className="product-review-mobile-navbar">
                    <MobileDetailNavbar cartCount={cartCount} fallbackPath="/Orders" />
                </div>
                <div className="product-review-desktop-secondary-navbar">
                    <HomeSecondaryNavbar
                        variant="detail"
                        browsingHistoryOpen={browsingHistoryOpen}
                        onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)}
                    />
                </div>
                <main className="product-review-page">
                    <div className="product-review-shell">
                        <section className="product-review-not-eligible">
                            <div className="product-review-lock-icon">
                                <ShieldAlert size={38} />
                            </div>
                            <span>AUTHENTICATION REQUIRED</span>
                            <h1>Sign In to Review</h1>
                            <p>
                                Only verified customers who purchased this item can write a review.
                                Please sign in to verify your purchase.
                            </p>
                            <div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate("/SignIn", {
                                            state: {
                                                returnUrl: `/ProductReviewPage/${productId}`,
                                            },
                                        })
                                    }
                                >
                                    Sign In
                                </button>
                                <button type="button" onClick={() => navigate(`/DetailPage/${productId}`)}>
                                    Return to Product
                                </button>
                            </div>
                        </section>
                    </div>
                </main>
            </>
        );
    }

    // Gating: If logged in but no qualifying order found and no existing review to view/edit
    if (!matchedOrder && !ordersLoading && !hasExistingReview) {
        return (
            <>
                <div className="product-review-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <div className="product-review-mobile-navbar">
                    <MobileDetailNavbar cartCount={cartCount} fallbackPath="/Orders" />
                </div>
                <div className="product-review-desktop-secondary-navbar">
                    <HomeSecondaryNavbar
                        variant="detail"
                        browsingHistoryOpen={browsingHistoryOpen}
                        onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)}
                    />
                </div>
                <main className="product-review-page">
                    <div className="product-review-shell">
                        <section className="product-review-not-eligible">
                            <div className="product-review-lock-icon">
                                <ShieldAlert size={38} />
                            </div>
                            <span>VERIFIED PURCHASE REQUIRED</span>
                            <h1>Only Verified Buyers Can Review</h1>
                            <p>
                                Reviews are reserved for customers who completed a purchase and
                                payment or delivery for this product.
                            </p>
                            <div>
                                <button type="button" onClick={() => navigate(`/DetailPage/${productId}`)}>
                                    Return to Product
                                </button>
                                <button type="button" onClick={() => navigate("/Orders")}>
                                    View Your Orders
                                </button>
                            </div>
                        </section>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <div className="product-review-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <div className="product-review-mobile-navbar">
                <MobileDetailNavbar
                    cartCount={cartCount}
                    fallbackPath={
                        matchedOrder?.orderId
                            ? `/MaxOrderDetails?id=${matchedOrder.orderId}`
                            : "/Orders"
                    }
                />
            </div>
            <div className="product-review-desktop-secondary-navbar">
                <HomeSecondaryNavbar
                    variant="detail"
                    browsingHistoryOpen={browsingHistoryOpen}
                    onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)}
                />
            </div>
            <main className="product-review-page">
                <div className="product-review-shell">
                    <div className="product-review-breadcrumb">
                        <button type="button" onClick={() => navigate("/Orders")}>
                            Your Orders
                        </button>
                        <ChevronRight size={15} />
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    matchedOrder?.orderId
                                        ? `/MaxOrderDetails?id=${matchedOrder.orderId}`
                                        : "/Orders"
                                )
                            }
                        >
                            Order Details
                        </button>
                        <ChevronRight size={15} />
                        <strong>
                            {isLockedMode
                                ? "View Product Review"
                                : isEditMode
                                ? "Edit Product Review"
                                : "Write Product Review"}
                        </strong>
                    </div>

                    {isLockedMode && (
                        <div
                            style={{
                                background: "#fffbeb",
                                border: "1px solid #fde68a",
                                color: "#92400e",
                                padding: "14px 18px",
                                borderRadius: "8px",
                                marginBottom: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                fontSize: "14px",
                            }}
                        >
                            <Lock size={22} style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Review Locked</strong> — You submitted this review on{" "}
                                {existingReview?.createdAt
                                    ? new Date(existingReview.createdAt).toLocaleDateString("en-IN", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                      })
                                    : "earlier"}
                                . As per Salar review guidelines, reviews can only be updated within 5 days of submission. Your review is now permanent.
                            </div>
                        </div>
                    )}

                    {isEditMode && (
                        <div
                            style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                color: "#166534",
                                padding: "12px 16px",
                                borderRadius: "8px",
                                marginBottom: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                fontSize: "14px",
                            }}
                        >
                            <Clock size={20} style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Editing Your Review</strong> — You have{" "}
                                <strong>{editDaysLeft} day(s) left</strong> in your 5-day edit window to adjust your rating, feedback, or photos.
                            </div>
                        </div>
                    )}

                    <section className="product-review-hero">
                        <div>
                            <span>
                                {isLockedMode
                                    ? "REVIEW LOCKED (5-DAY WINDOW CLOSED)"
                                    : isEditMode
                                    ? "EDITING YOUR REVIEW"
                                    : "VERIFIED PURCHASE REVIEW"}
                            </span>
                            <h1>
                                {isLockedMode
                                    ? "Your Submitted Review"
                                    : isEditMode
                                    ? "Update Your Purchase Review"
                                    : "Review Your Purchase"}
                            </h1>
                            <p>
                                {isLockedMode
                                    ? "Customer reviews can only be modified within 5 days of submission. Your review is now permanent and view-only."
                                    : isEditMode
                                    ? `You submitted this review on ${
                                          existingReview?.createdAt
                                              ? new Date(existingReview.createdAt).toLocaleDateString("en-IN")
                                              : "earlier"
                                      }. You can update your rating or feedback while the 5-day window is active.`
                                    : "Share what you liked, what could be better and how the product worked for you. Your review helps other Salar customers understand the product before they buy."}
                            </p>
                        </div>
                        <div className="product-review-order-ref">
                            <span>ORDER ID</span>
                            <strong>{orderRefId}</strong>
                            <small> {orderPlacedDate}</small>
                        </div>
                    </section>
                    <section className="product-review-card product-review-product-card">
                        <button type="button" className="product-review-product-image" onClick={() => openProduct()}>
                            <img src={displayImage} alt={displayTitle} />
                        </button>
                        <div className="product-review-product-copy">
                            <span>HOW WAS THE ITEM?</span>
                            <button type="button" onClick={() => openProduct()}>
                                <strong>{displayTitle}</strong>
                                <p>{displayDescription}</p>
                            </button>
                            <div className="product-review-product-meta">
                                {matchedProductDetail?.productType && (
                                    <span>{matchedProductDetail.productType}</span>
                                )}
                                <span>Qty {matchedProductDetail?.quantity || 1}</span>
                                <span className="verified">
                                    <BadgeCheck size={14} />
                                    Verified Purchase
                                </span>
                            </div>
                        </div>
                    </section>
                    <section className="product-review-card product-review-form-card">
                        <div className="product-review-section">
                            <div className="product-review-section-head">
                                <h2>Rate this product</h2>
                                <p> Select the rating that best matches your overall product experience. </p>
                            </div>
                            <div className="product-review-rating-bar">
                                <div className="product-review-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            type="button"
                                            key={star}
                                            disabled={isLockedMode}
                                            style={isLockedMode ? { cursor: "default" } : {}}
                                            className={star <= rating ? "selected" : ""}
                                            onClick={() => {
                                                if (isLockedMode) return;
                                                setRating(star);
                                                setError("");
                                            }}
                                            aria-label={`${star} star rating`}
                                        >
                                            <Star size={29} fill={star <= rating ? "currentColor" : "none"} />
                                        </button>
                                    ))}
                                </div>
                                <div className="product-review-rating-result">
                                    <span>YOUR RATING</span>
                                    <strong>{ratingLabel}</strong>
                                    <small> {rating > 0 ? `${rating} of 5 stars` : "Tap a star to rate"} </small>
                                </div>
                            </div>
                        </div>
                        <div className="product-review-divider" />
                        <div className="product-review-section">
                            <div className="product-review-section-head">
                                <h2>Write a review</h2>
                                <p> Tell customers what stood out about the quality, fit, comfort, design, performance or everyday use. </p>
                            </div>
                            <label className="product-review-textarea">
                                <textarea
                                    value={review}
                                    maxLength={1200}
                                    disabled={isLockedMode}
                                    onChange={(event) => setReview(event.target.value)}
                                    placeholder="What should other customers know about this product?"
                                />
                                <span> {review.length}/1200 </span>
                            </label>
                        </div>
                        <div className="product-review-divider" />
                        <div className="product-review-section">
                            <div className="product-review-section-head">
                                <h2> Share a photo or video </h2>
                                <p> Optional. Add up to 6 photos or videos to show the product in real use.</p>
                            </div>
                            {!isLockedMode && (
                                <input
                                    ref={fileInputRef}
                                    className="product-review-file-input"
                                    type="file"
                                    accept="image/*,video/*"
                                    multiple
                                    onChange={handleMediaChange}
                                />
                            )}
                            {!isLockedMode && media.length < MAX_MEDIA && (
                                <button
                                    type="button"
                                    className="product-review-media-upload"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="product-review-media-upload-icon">
                                        <ImagePlus size={30} />
                                    </div>
                                    <div>
                                        <strong> Add photo or video </strong>
                                        <span> Choose up to 6 images or videos from your device </span>
                                    </div>
                                </button>
                            )}
                            {media.length > 0 && (
                                <div className="product-review-uploaded-wrap">
                                    <div className="product-review-uploaded-head">
                                        <div>
                                            <strong> Uploaded media </strong>
                                            <span> {media.length} of {MAX_MEDIA} added </span>
                                        </div>
                                        {!isLockedMode && media.length < MAX_MEDIA && (
                                            <button type="button" onClick={() => fileInputRef.current?.click()}>
                                                <ImagePlus size={17} /> Add More
                                            </button>
                                        )}
                                    </div>
                                    <div className="product-review-media-grid">
                                        {media.map((file) => (
                                            <div className="product-review-media-preview" key={file.id}>
                                                {file.type === "image" ? (
                                                    <img src={file.previewUrl} alt={file.name} />
                                                ) : (
                                                    <>
                                                        <video src={file.previewUrl} muted controls />
                                                        <span className="product-review-video-badge">
                                                            <Play size={17} fill="currentColor" />
                                                        </span>
                                                    </>
                                                )}

                                                <div className="product-review-media-name"> {file.name}</div>
                                                {!isLockedMode && (
                                                    <button
                                                        type="button"
                                                        className="product-review-media-remove"
                                                        onClick={() => removeMedia(file.id)}
                                                        aria-label={`Remove ${file.name}`}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="product-review-divider" />
                        <div className="product-review-section product-review-fields-section">
                            <label>
                                <span> Title your review <strong>Required</strong> </span>
                                <input
                                    type="text"
                                    value={title}
                                    maxLength={120}
                                    disabled={isLockedMode}
                                    onChange={(event) => {
                                        setTitle(event.target.value);
                                        setError("");
                                    }}
                                    placeholder="What's most important to know?"
                                />
                                <small> {title.length}/120 </small>
                            </label>
                            <label>
                                <span> Your public name <strong>Required</strong> </span>
                                <div className="product-review-name-input">
                                    <UserRound size={18} />
                                    <input
                                        type="text"
                                        value={publicName}
                                        maxLength={60}
                                        disabled={isLockedMode}
                                        onChange={(event) => {
                                            setPublicName(event.target.value);
                                            setError("");
                                        }}
                                        placeholder="Public name"
                                    />
                                </div>
                                <p> This name appears publicly with your review. </p>
                            </label>
                        </div>
                        <div className="product-review-guidance">
                            <Info size={19} />
                            <div>
                                <strong> Keep the review about the product </strong>
                                {productReviewTips.map((tip) => (
                                    <span key={tip}>{tip}</span>
                                ))}
                            </div>
                        </div>
                        {error && (
                            <div className="product-review-error">
                                <Info size={18} />
                                <span>{error}</span>
                            </div>
                        )}
                        <div className="product-review-submit-row">
                            {isLockedMode ? (
                                <>
                                    <button
                                        type="button"
                                        className="product-review-cancel"
                                        onClick={() => openProduct()}
                                    >
                                        Return to Product
                                    </button>
                                    <button
                                        type="button"
                                        className="product-review-submit"
                                        disabled
                                        style={{
                                            opacity: 0.6,
                                            cursor: "not-allowed",
                                            background: "#6b7280",
                                        }}
                                    >
                                        Review Locked
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="product-review-cancel"
                                        onClick={() =>
                                            navigate(
                                                matchedOrder?.orderId
                                                    ? `/MaxOrderDetails?id=${matchedOrder.orderId}`
                                                    : "/Orders"
                                            )
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="product-review-submit"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                    >
                                        {submitting
                                            ? "Saving..."
                                            : isEditMode
                                            ? "Update Review"
                                            : "Submit Review"}
                                    </button>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </>
    );
};

export default ProductReviewPage;
